<script>
  import Loading from "../../../../components/pages/Loading.svelte";
  import { useLicenseData } from "../../../../hooks/license/useLicenseData.js";
  import DialogTitle from "../../../../components/dialog/DialogTitle.svelte";
  import DialogSplitter from "../../../../components/dialog/DialogSplitter.svelte";
  import DialogContent from "../../../../components/dialog/DialogContent.svelte";
  import DialogBottomControls from "../../../../components/dialog/DialogBottomControls.svelte";
  import DialogBottomControlButton from "../../../../components/dialog/DialogBottomControlButton.svelte";
  import {
    useOnlineLicenseAuthenticationResult
  } from "../../../../hooks/license/useOnlineLicenseAuthenticationResult.js";
  import { translate } from "../../../../i18n/i18next.js";
  import { z } from "zod";
  import { createForm } from "felte"
  import { validator } from "@felte/validator-zod"
  import { getNotificationsContext } from "svelte-notifications"

  export let onClickClose

  /**
   * @function
   * @param {{
   *   asprovaId: string,
   *   password: string,
   *   token: string,
   * }} data
   */
  export let moveNext = (data) => {}

  const licenseData = useLicenseData()
  const authResult = useOnlineLicenseAuthenticationResult()
  const {addNotification} = getNotificationsContext()

  const schema = z.object({
    asprovaId: z.string().email(),
    password: z.string().min(1),
  })

  let submissionError = ""
  let isSubmitting = false
  function setSubmissionError(text) {
    submissionError = text
    setTimeout(() => {
      if (submissionError === text) submissionError = ""
    }, 3000)
  }
  async function handleSubmit(submissionData) {
    submissionError = ""

    if (!submissionData.asprovaId || !submissionData.password) return
    try {
      isSubmitting = true
      const response = await fetch("/api/license/online/token", {
        method: "POST",
        body: JSON.stringify({username: submissionData.asprovaId, password: submissionData.password, scope: "licenselist"}),
        headers: {
          "Content-Type": "application/json",
        }
      })
      isSubmitting = false
      if (response.ok) {
        const responseData = await response.json()
        moveNext({
          accessToken: responseData.accessToken,
          asprovaId: submissionData.asprovaId,
          password: submissionData.password,
        })
      } else {
        addNotification({
          text: translate("frontend:components.changeLicenseAuthenticationMethodDialog.loginAsprovaId.messages.failedToLogin"),
          type: "error",
          removeAfter: 4000,
          position: "top-right",
        })
        setSubmissionError(translate("frontend:components.changeLicenseAuthenticationMethodDialog.loginAsprovaId.messages.failedToLogin"))
      }
    } catch (e) {
      addNotification({
        text: translate("frontend:components.changeLicenseAuthenticationMethodDialog.loginAsprovaId.messages.networkError"),
        type: "error",
        removeAfter: 4000,
        position: "top-right",
      })
      setSubmissionError(translate("frontend:components.changeLicenseAuthenticationMethodDialog.loginAsprovaId.messages.networkError"))
    }
  }
  const {form, errors, createSubmitHandler} = createForm({
    onSubmit: handleSubmit,
    initialValues: {},
    extend: validator({schema})
  })
  const submitForm = createSubmitHandler()
</script>

<DialogTitle title={translate("frontend:components.changeLicenseAuthenticationMethodDialog.loginAsprovaId.title")}/>
<DialogSplitter/>
<DialogContent>
  {#if $authResult && $licenseData}
    {#if $authResult.status === "using"}
      <div>
        {translate("frontend:components.changeLicenseAuthenticationMethodDialog.loginAsprovaId.messages.alreadyAuthenticated")}
      </div>
    {:else}
      <div>
        {translate("frontend:components.changeLicenseAuthenticationMethodDialog.loginAsprovaId.caption")}
      </div>
    {/if}
    <form use:form>
      <div class="input-container">
        <label>
          Asprova ID:
          <input
            autocomplete="off"
            name="asprovaId"
            placeholder={"Asprova ID"}
          />
        </label>
        {#if $errors.asprovaId}
          <span class="error">{$errors.asprovaId}</span>
        {/if}
      </div>
      <div class="input-container">
        <label>
          {translate("generic.password")}:
          <input
            type="password"
            name="password"
            autocomplete="new-password"
            placeholder={translate("generic.password")}
          />
        </label>
        {#if $errors.password}
          <span class="error">{$errors.password}</span>
        {/if}
      </div>
      {#if submissionError}
        <span class="error" style="align-self: center">{submissionError}</span>
      {/if}
    </form>
  {:else}
    <Loading height="200px"/>
  {/if}
</DialogContent>
<DialogSplitter/>
<DialogBottomControls>
  {#if $authResult?.status !== "using"}
    <DialogBottomControlButton type="primary" disabled={isSubmitting} onClick={submitForm}>
      {translate("generic.login")}
    </DialogBottomControlButton>
  {/if}
  <DialogBottomControlButton type="secondary" onClick={onClickClose}>
    {translate("generic.cancel")}
  </DialogBottomControlButton>
</DialogBottomControls>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
  input {
    font-size: 1rem;
    padding: 0.4rem;
    width: 300px;
    outline: none;
    border-radius: 5px;
    border: 1px solid black;
    background-color: white;
  }

  input:focus {
    outline: 2px solid #006bdf;
    outline-offset: -1px;
  }

  .input-container {
    display: flex;
    flex-direction: column;
    gap: .3rem;
  }
  .error {
    color: red;
    font-size: 0.9rem;
    align-self: end;
  }
</style>
